<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { X } from 'lucide-vue-next'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

interface Props {
  /** v-model:open. Accepts boolean OR any truthy value (e.g. a member to delete). */
  modelValue: boolean | unknown
  title?: string
  size?: ModalSize
  /** show the X close button (default true). Set false for non-dismissable flows. */
  closable?: boolean
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  closable: true,
  closeOnBackdrop: true,
  closeOnEscape: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
}>()

const isOpen = computed(() => Boolean(props.modelValue))
const dialogRef = ref<HTMLElement | null>(null)
const previouslyFocused = ref<HTMLElement | null>(null)

function getFocusable(): HTMLElement[] {
  if (!dialogRef.value) return []
  const sel =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  return Array.from(dialogRef.value.querySelectorAll<HTMLElement>(sel))
}

function focusFirst(): void {
  const els = getFocusable()
  if (els.length > 0) {
    els[0]?.focus()
  } else if (dialogRef.value) {
    dialogRef.value.focus()
  }
}

function close(): void {
  if (!props.closable) {
    emit('close')
    return
  }
  emit('update:modelValue', false)
  emit('close')
}

function onKeydown(e: KeyboardEvent): void {
  if (!isOpen.value) return
  if (e.key === 'Escape' && props.closeOnEscape) {
    e.stopPropagation()
    close()
    return
  }
  if (e.key === 'Tab') {
    // Focus trap — keep Tab within the dialog
    const els = getFocusable()
    if (els.length === 0) {
      e.preventDefault()
      dialogRef.value?.focus()
      return
    }
    const first = els[0]
    const last = els[els.length - 1]
    const active = document.activeElement as HTMLElement | null
    if (e.shiftKey) {
      if (active === first || !dialogRef.value?.contains(active)) {
        e.preventDefault()
        last?.focus()
      }
    } else {
      if (active === last) {
        e.preventDefault()
        first?.focus()
      }
    }
  }
}

function lockScroll(lock: boolean): void {
  if (typeof document === 'undefined') return
  document.body.style.overflow = lock ? 'hidden' : ''
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  if (isOpen.value) lockScroll(true)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  lockScroll(false)
})

watch(isOpen, async (v) => {
  lockScroll(v)
  if (v) {
    // Remember the previously focused element so we can restore on close.
    previouslyFocused.value = (document.activeElement as HTMLElement | null) ?? null
    await nextTick()
    focusFirst()
  } else if (previouslyFocused.value && typeof previouslyFocused.value.focus === 'function') {
    // Restore focus to the element that opened the modal.
    previouslyFocused.value.focus()
    previouslyFocused.value = null
  }
})

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  full: 'max-w-[min(96vw,96rem)] w-full h-[90vh]',
}
</script>

<template>
  <Teleport to="body">
    <transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="closeOnBackdrop && close()"
        />
        <div
          ref="dialogRef"
          tabindex="-1"
          class="relative flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-lg border border-border bg-bg-surface shadow-xl focus:outline-none"
          :class="sizeClass[size]"
        >
          <div
            v-if="title || $slots.title || $slots.actions || closable"
            class="flex shrink-0 items-center justify-between border-b border-border px-5 py-3"
          >
            <h3 class="text-base font-semibold text-fg">
              <slot name="title">{{ title }}</slot>
            </h3>
            <div class="flex items-center gap-2">
              <slot name="actions" />
              <button
                v-if="closable"
                type="button"
                class="rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
                aria-label="Close"
                @click="close"
              >
                <X :size="16" />
              </button>
            </div>
          </div>
          <!-- v3.13.81: body 统一可滚动 + 面板 90dvh 封顶 — 修复手机上内容
               超高时提交按钮被顶出屏幕 (AddTaskDialog 高级选项) -->
          <div class="min-h-0 flex-1 overflow-y-auto p-5">
            <slot />
          </div>
          <div
            v-if="$slots.footer"
            class="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 200ms ease;
}
.modal-enter-active > div:last-child,
.modal-leave-active > div:last-child {
  transition: transform 200ms ease, opacity 200ms ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from > div:last-child,
.modal-leave-to > div:last-child {
  transform: scale(0.96);
  opacity: 0;
}
</style>
